import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Mail } from 'lucide-react';
import { COMPANY_INFO } from '@/constants/company';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${siteUrl}/contact#webpage`,
  url: `${siteUrl}/contact`,
  name: 'お問い合わせ｜名古屋不動産売却・買取 株式会社クリアコーポレーション',
  description: '名古屋の不動産売却・買取・査定に関するお問い合わせはこちら。フォームまたはお電話（052-325-5812）でご相談ください。営業時間9:00〜17:00。株式会社クリアコーポレーション。',
  inLanguage: 'ja',
  mainEntity: {
    '@type': 'RealEstateAgent',
    name: '株式会社クリアコーポレーション',
    telephone: '052-325-5812',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '江向町五丁目15番地1',
      addressLocality: '名古屋市西区',
      addressRegion: '愛知県',
      postalCode: '451-0013',
      addressCountry: 'JP',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '09:00',
        closes: '17:00',
      },
    ],
  },
};

export default function ContactPage() {
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    propertyAddress: '',
    message: '',
  });

  useEffect(() => {
    if (location.hash === '#form') {
      setTimeout(() => {
        const el = document.getElementById('form');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission is handled by the form action
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (submitted) {
    return (
      <>
        <title>お問い合わせ｜名古屋不動産売却・買取 株式会社クリアコーポレーション</title>
        <meta name="description" content="名古屋の不動産売却・買取・査定に関するお問い合わせはこちら。フォームまたはお電話（052-325-5812）でご相談ください。営業時間9:00〜17:00。株式会社クリアコーポレーション。" />
        <meta name="keywords" content="クリアコーポレーション お問い合わせ, 名古屋 不動産 相談, 名古屋 不動産売却 問い合わせ" />
        <meta property="og:title" content="お問い合わせ｜名古屋不動産売却・買取 株式会社クリアコーポレーション" />
        <meta property="og:description" content="名古屋の不動産売却・買取・査定に関するお問い合わせはこちら。フォームまたはお電話でご相談ください。" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://clearcorp.info/contact" />
        <script type="application/ld+json">{JSON.stringify(contactJsonLd)}</script>
        <Header />
        <main className="min-h-screen bg-slate-50">
          <section className="py-24 md:py-32">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  お問い合わせありがとうございます
                </h1>
                <p className="text-slate-600 mb-8">
                  内容を確認次第、担当者よりご連絡させていただきます。
                  <br />
                  通常、2営業日以内にご返信いたします。
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/"
                    className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
                  >
                    トップページへ戻る
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>お問い合わせ｜名古屋不動産売却・買取 株式会社クリアコーポレーション</title>
      <meta name="description" content="名古屋の不動産売却・買取・査定に関するお問い合わせはこちら。フォームまたはお電話（052-325-5812）でご相談ください。営業時間9:00〜17:00。株式会社クリアコーポレーション。" />
      <meta name="keywords" content="クリアコーポレーション お問い合わせ, 名古屋 不動産 相談, 名古屋 不動産売却 問い合わせ" />
      <meta property="og:title" content="お問い合わせ｜名古屋不動産売却・買取 株式会社クリアコーポレーション" />
      <meta property="og:description" content="名古屋の不動産売却・買取・査定に関するお問い合わせはこちら。フォームまたはお電話でご相談ください。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/contact" />
      <script type="application/ld+json">{JSON.stringify(contactJsonLd)}</script>
      <Header />
      <main className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              お問い合わせ
            </h1>
            <p className="text-slate-300 text-lg">
              不動産に関するご相談・ご質問は、こちらのフォームからお気軽にお問い合わせください。
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-12 -mt-8">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">お電話</h3>
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="text-xl font-bold text-emerald-600 hover:text-emerald-700"
                >
                  {COMPANY_INFO.phone}
                </a>
                <p className="text-slate-500 text-sm mt-1">
                  {COMPANY_INFO.businessHours}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">メール相談</h3>
                <p className="text-slate-600 text-sm mb-3">
                  24時間受付・無料
                </p>
                <span className="text-emerald-600 font-semibold">
                  フォームよりご連絡ください
                </span>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">所在地</h3>
                <p className="text-slate-600 text-sm">
                  {COMPANY_INFO.address}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="form" className="py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-10">
              <h2 className="text-xl font-bold text-slate-900 mb-6">
                お問い合わせフォーム
              </h2>

              <form
                action="https://readdy.ai/api/form/d77j29t5uau42b5in9v0"
                method="POST"
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-6"
              >
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="052-0000-0000"
                  />
                </div>

                {/* Inquiry Type */}
                <div>
                  <label htmlFor="inquiryType" className="block text-sm font-semibold text-slate-900 mb-2">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    required
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  >
                    <option value="">選択してください</option>
                    <option value="selling">不動産売却について</option>
                    <option value="purchase">不動産買取について</option>
                    <option value="buying">物件購入について</option>
                    <option value="inheritance">相続・空き家について</option>
                    <option value="appraisal">査定について</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                {/* Property Address */}
                <div>
                  <label htmlFor="propertyAddress" className="block text-sm font-semibold text-slate-900 mb-2">
                    物件所在地（該当する場合）
                  </label>
                  <input
                    type="text"
                    id="propertyAddress"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    placeholder="愛知県名古屋市..."
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all resize-none"
                    placeholder="お問い合わせ内容をご記入ください..."
                  />
                  <p className="text-slate-400 text-xs mt-1 text-right">
                    {formData.message.length}/500文字
                  </p>
                </div>

                {/* Privacy Policy */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-slate-600 text-sm leading-relaxed">
                    ご記入いただいた個人情報は、お問い合わせへの対応および不動産に関するご案内のみに使用し、
                    第三者への提供はいたしません。詳細は
                    <a href="/privacy" className="text-emerald-600 hover:underline">
                      プライバシーポリシー
                    </a>
                    をご確認ください。
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 rounded-lg transition-colors"
                >
                  送信する
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              まずはお気軽にご相談ください
            </h2>
            <p className="text-slate-300 mb-8">
              電話・メールどちらでも無料でご相談いただけます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-10 py-5 rounded-lg transition-colors text-lg"
              >
                <Phone className="w-6 h-6" />
                電話で相談する
              </a>
              <a
                href="#form"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-10 py-5 rounded-lg transition-colors text-lg border border-white/30"
              >
                <Mail className="w-6 h-6" />
                メールで相談する
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
